import { CollectionReference, DocumentData, Query, WhereFilterOp, doc, getDoc, getFirestore, query, where } from "firebase/firestore";
import { ArrayUtilities } from "../../../utils/arrayUtilities";
import { StringUtilities } from "../../../utils/stringUtilities";
import { Utilities } from "../../../utils/utilities";
import { CompanyData } from "../Company/CompanyData";
import { DeviationData } from "../Deviation/DeviationData";
import { PropertyCollectionItemData } from "../Property/Collections/PropertyCollectionItemData";
import { MemberData } from "../Property/MemberData";
import { PropertyData } from "../Property/PropertyData";
import { RoutineData } from "../Routines/RoutineData";
import { UserData } from "../User/UserData";
import CollectionLoader from "./CollectionLoader";
import { FileDocumentData } from "./FileDocumentData";
import { FirebaseData } from "./FirebaseData";
import { LocalData } from "./LocalData";
import { PropertyFileData } from "./PropertyFileData";

export type DataSourceValueType = FirebaseData | string | number | object | DataSourceValueType[];
export type DataSourceResponse = {
  id?: string;
  values: DataSourceValueType[];
}[];

export abstract class DataSource {
  // @ts-ignore - unused param.
  constructor(data: any) {}
  abstract getValue(property?: PropertyData): Promise<DataSourceResponse>;
  abstract getFirebaseData(): { [key: string]: any };
  abstract copy(): DataSource;

  static getDataSource(data: any): DataSource | undefined {
    if (!data) return undefined;

    if (Object.keys(data).includes("value")) {
      return new ValueDataSource(data.value);
    } else if (Object.keys(data).includes("reference")) {
      return new ReferenceDataSource(data.reference);
    }
    return undefined;
  }
  static areDataSourcesEqual(a: DataSource | undefined, b: DataSource | undefined): boolean {
    if (a?.constructor !== b?.constructor) return false;

    if (a instanceof ValueDataSource && b instanceof ValueDataSource) {
      return a.data === b.data;
    } else if (a instanceof ReferenceDataSource && b instanceof ReferenceDataSource) {
      return ArrayUtilities.areArraysEqual(a.steps, b.steps);
    }

    return false;
  }

  static getStringFromField(field: DataSourceValueType): string {
    if (field === undefined) return "";
    if (Array.isArray(field)) {
      return field.sort((a, b) => (a.toString() < b.toString() ? -1 : 1)).join(", ");
    }
    if (typeof field === "object") {
      let value = "";
      if ((field as any).value) value = (field as any).value.toString();
      if ((field as any).suffix) value += " " + (field as any).suffix;
      return value;
    }
    return field.toString();
  }
}

export class ValueDataSource extends DataSource {
  data: any;
  constructor(data: any) {
    super(data);
    this.data = data.data;
  }
  getValue(): Promise<DataSourceResponse> {
    return Promise.resolve([{ values: [this.data] }]);
  }
  getFirebaseData() {
    if (this.data !== undefined) {
      return {
        value: {
          data: this.data,
        },
      };
    } else {
      return {
        value: {},
      };
    }
  }
  copy(): ValueDataSource {
    return new ValueDataSource(this.getFirebaseData().value);
  }
}

type DataSourceCollectionLoader =
  | CollectionLoader<PropertyCollectionItemData>
  | CollectionLoader<DeviationData>
  | CollectionLoader<RoutineData>
  | CollectionLoader<MemberData>
  | CollectionLoader<UserData>
  | CollectionLoader<CompanyData>
  | CollectionLoader<FileDocumentData>
  | CollectionLoader<PropertyFileData>;
type ReferenceQuery = {
  field: string;
  equals?: any;
  notEquals?: any;
  max?: any;
  min?: any;
  containsAny?: any[];
};
export class ReferenceDataSource extends DataSource {
  steps: {
    path: string;
    fields?: string[];
    queries?: ReferenceQuery[];
  }[] = [];

  constructor(data: any) {
    super(data);
    this.steps = Array.isArray(data.steps) ? data.steps : undefined;
  }

  getValue(property?: PropertyData): Promise<DataSourceResponse> {
    if (!this.steps || this.steps.length === 0) {
      return Promise.resolve([]);
    } else {
      return new Promise((resolve, reject) => {
        let result: DataSourceResponse = [];

        const loadStep = (index: number, previousSteps: DataSourceValueType[]) => {
          return new Promise<void>((resolve, reject) => {
            const step = this.steps[index];
            let path = step.path;
            for (let i in previousSteps) {
              const previousStep = previousSteps[i];
              if (previousStep instanceof FirebaseData) path = StringUtilities.replaceAll(path, "$" + i, previousStep.id);
              if (typeof previousStep === "string") path = StringUtilities.replaceAll(path, "$" + i, previousStep);
            }

            if (step.fields && step.fields.length > 0) {
              let pathElements = path.split("/");
              this.loadFromField(step.fields, path)
                .then((value) => {
                  if (this.steps.length > index + 1) {
                    return Promise.all(value?.map((doc) => loadStep(index + 1, [...previousSteps, doc])) ?? []).finally(() => {
                      resolve();
                    });
                  } else {
                    if (value) {
                      result?.push({ id: pathElements[pathElements.length - 1], values: value });
                    }
                    resolve();
                  }
                })
                .catch(() => {
                  reject();
                });
            } else if (step.queries) {
              const loader = ReferenceDataSource.getCollectionLoader(path);

              if (loader && step.queries) {
                this.loadFromQueries(loader, step.queries, property)
                  .then((docs) => {
                    if (this.steps.length > index + 1) {
                      return Promise.all(docs.map((doc) => loadStep(index + 1, [...previousSteps, doc]))).finally(() => {
                        resolve();
                      });
                    } else {
                      for (const doc of docs) {
                        result.push({ id: doc.id, values: [doc] });
                      }
                      resolve();
                    }
                  })
                  .catch((error) => {
                    console.log("Error loading from queries " + error);
                    reject();
                  });
              } else {
                console.log("No fields or queries");
                reject();
              }
            } else {
              this.loadFirebaseData(path)
                .then((val) => {
                  if (val) result.push({ id: val.id, values: [val] });
                })
                .finally(() => resolve());
            }
          });
        };

        loadStep(0, [])
          .then(() => {
            resolve(result);
          })
          .catch(() => {
            reject();
          });
      });
    }
  }

  getFirebaseData() {
    return {
      reference: {
        steps: this.steps,
      },
    };
  }

  copy(): ReferenceDataSource {
    return new ReferenceDataSource(this.getFirebaseData().reference);
  }

  async loadFromField(fields: string[], path: string): Promise<DataSourceValueType[]> {
    const db = getFirestore();
    const docRef = doc(db, path);

    if (path.split("/").some((el) => el.trim().length === 0)) return Promise.resolve([]);

    return new Promise((resolve, reject) => {
      getDoc(docRef)
        .then((snapshot) => {
          if (snapshot && snapshot.exists()) {
            let data = snapshot.data();
            let value: DataSourceValueType[] = [];
            for (const field of fields) {
              let fieldData = data;
              for (const fieldElement of field.split(".")) {
                if (data !== undefined && fieldData !== undefined) {
                  fieldData = fieldData[fieldElement];
                }
              }
              if (typeof fieldData === "string") value.push(fieldData);
              else if (typeof fieldData === "number") value.push(fieldData);
              else if (Array.isArray(fieldData)) value.push(fieldData);
              else if (Utilities.isValidObject(fieldData) && fieldData) value.push(fieldData);
              else value.push("");
            }
            resolve(value);
          }
        })
        .catch((error) => {
          console.log("Error loading document from " + path);
          console.log(error.message);
          resolve([]);
        });
    });
  }

  async loadFromQueries(collectionLoader: DataSourceCollectionLoader, queries: ReferenceQuery[], property?: PropertyData): Promise<FirebaseData[]> {
    let pathElements = collectionLoader.getPath().split("/");
    return new Promise((resolve, reject) => {
      type queryRef = {
        a: string;
        op: WhereFilterOp;
        b: any;
      };
      let firebaseQueries: queryRef[] = [];
      let localQueries: ReferenceQuery[] = [];
      function filterRef(ref: CollectionReference<DocumentData>): Query {
        if (pathElements[pathElements.length - 1] === "routines" && property) {
          let companyIds = property.companies.filter((company) => LocalData.currentUser.companies.includes(company)).map((company) => company.id);
          if (companyIds.length > 0) firebaseQueries.push({ a: "companies", op: "array-contains-any", b: companyIds });
        }
        if (pathElements[pathElements.length - 1] === "items" && property) {
          let member = property.members.addData(LocalData.currentUser.id);
          const memberRoles = member.roles.map((role) => role.id);
          if (memberRoles.length) {
            firebaseQueries.push({ a: "readAccess.roles", op: "array-contains-any", b: memberRoles });
          }
        }
        for (let query of queries) {
          if (firebaseQueries.length < 2) {
            if (query.equals !== undefined) firebaseQueries.push({ a: query.field, op: "==", b: query.equals });
            if (query.notEquals !== undefined) {
              if (firebaseQueries.length === 0) firebaseQueries.push({ a: query.field, op: "!=", b: query.notEquals });
              else localQueries.push(query);
            }
            if (query.max !== undefined) firebaseQueries.push({ a: query.field, op: "<=", b: query.max });
            if (query.min !== undefined) firebaseQueries.push({ a: query.field, op: ">=", b: query.min });
            if (query.containsAny !== undefined) {
              if (!firebaseQueries.find((fQuery) => fQuery.op === "array-contains-any") && query.containsAny?.length)
                firebaseQueries.push({ a: query.field, op: "array-contains-any", b: query.containsAny });
              else localQueries.push(query);
            }
          } else {
            if (query.equals !== undefined) localQueries.push(query);
            if (query.notEquals !== undefined) localQueries.push(query);
            if (query.max !== undefined) localQueries.push(query);
            if (query.min !== undefined) localQueries.push(query);
            if (query.containsAny !== undefined) localQueries.push(query);
          }
        }

        if (firebaseQueries.length === 0) return ref;
        else if (firebaseQueries.length === 1) return query(ref, where(firebaseQueries[0].a, firebaseQueries[0].op, firebaseQueries[0].b));
        else
          return firebaseQueries
            .slice(1, firebaseQueries.length)
            .reduce(
              (prev, current) => query(prev, where(current.a, current.op, current.b)),
              query(ref, where(firebaseQueries[0].a, firebaseQueries[0].op, firebaseQueries[0].b))
            );
      }
      collectionLoader.loadWithFilter(
        (ref) => {
          return filterRef(ref);
        },
        (documents: FirebaseData[]) => {
          resolve(
            documents.filter((doc) => {
              for (const query of localQueries) {
                let rawData = doc.rawData;
                for (const field of query.field.split(".")) {
                  if (rawData !== undefined) {
                    rawData = rawData[field];
                  } else if (query.notEquals !== undefined) {
                    return true;
                  }
                }

                if (query.notEquals !== undefined && rawData === (query.notEquals !== null ? query.notEquals : undefined)) return false;
                if (
                  query.containsAny !== undefined &&
                  (!rawData || !Array.isArray(rawData) || !rawData.find((element) => query.containsAny?.includes(element)))
                )
                  return false;
              }
              return true;
            })
          );
        }
      );
    });
  }

  async loadFirebaseData(path: string): Promise<FirebaseData | undefined> {
    const pathElements = path.split("/");
    const documentID = pathElements[pathElements.length - 1];
    pathElements.splice(pathElements.length - 1);
    const loader = ReferenceDataSource.getCollectionLoader(pathElements.join("/"));
    if (!loader || pathElements.some((el) => el.trim().length === 0)) return Promise.resolve(undefined);

    return new Promise((resolve, reject) => {
      loader.loadDocument(documentID, (doc: FirebaseData | undefined) => {
        resolve(doc);
      });
    });
  }

  static getCollectionLoader(path: string): DataSourceCollectionLoader | undefined {
    let pathElements = path.split("/");
    let propertyIndex = pathElements.indexOf("properties");
    let propertyId = pathElements[propertyIndex + 1];
    if (pathElements[pathElements.length - 1] === "deviations") {
      let property = LocalData.properties.addData(propertyId);
      return property.deviations;
    } else if (pathElements[pathElements.length - 1] === "routines") {
      return LocalData.routines;
    } else if (pathElements[pathElements.length - 1] === "users") {
      return new CollectionLoader<UserData>("users", undefined, UserData);
    } else if (pathElements[pathElements.length - 1] === "members") {
      return LocalData.properties.addData(propertyId).members;
    } else if (pathElements[pathElements.length - 1] === "files") {
      return LocalData.properties.addData(propertyId).files;
    } else if (pathElements[pathElements.length - 1] === "companies") {
      return LocalData.companies;
    } else {
      let property = LocalData.properties.addData(propertyId);
      if (pathElements[pathElements.length - 3] === "collections") {
        return property.collections.addData(pathElements[pathElements.length - 2]).items;
      } else {
        return undefined;
      }
    }
  }
}

